import { lazy } from "react";
import {
  Atom,
  Beaker,
  Binary,
  Blocks,
  BookOpen,
  Boxes,
  Brain,
  Calculator,
  CircleGauge,
  Compass,
  Dna,
  Droplets,
  Flame,
  FlaskConical,
  FunctionSquare,
  Gauge,
  Globe2,
  Grid3x3,
  HeartPulse,
  Layers,
  LineChart,
  Magnet,
  MoveHorizontal,
  Percent,
  PieChart,
  Radius,
  Repeat2,
  Ruler,
  Scale,
  Shapes,
  Sigma,
  SlidersHorizontal,
  Sparkles,
  Sprout,
  Square,
  Star,
  Target,
  Thermometer,
  Timer,
  TrendingUp,
  Triangle,
  Waves,
  Weight,
  Zap,
} from "lucide-react";
import type { ToolkitCategory, ToolkitTool } from "./types";

export const TOOLKIT_CATEGORIES: ToolkitCategory[] = [
  {
    id: "calculators",
    name: "Calculators",
    description: "Scientific, scores, percentile and planning maths",
    icon: Calculator,
    accent: "from-[var(--primary)] to-[var(--primary)]",
  },
  {
    id: "converters",
    name: "Conversion",
    description: "Every unit you meet in a JEE paper",
    icon: Repeat2,
    accent: "from-[#3ec98a] to-[var(--primary)]",
  },
  {
    id: "chemistry",
    name: "Chemistry",
    description: "Periodic table, reactions and analysis charts",
    icon: FlaskConical,
    accent: "from-[#f5c451] to-[#f2545b]",
  },
  {
    id: "physics",
    name: "Physics",
    description: "Units, constants, dimensions and equations",
    icon: Atom,
    accent: "from-[var(--primary)] to-[#3ec98a]",
  },
  {
    id: "maths",
    name: "Mathematics",
    description: "Formula sheets from trig to probability",
    icon: Sigma,
    accent: "from-[var(--primary)] to-[#f2545b]",
  },
  {
    id: "biology",
    name: "Biology",
    description: "NEET genetics, physiology, cell and ecology",
    icon: Dna,
    accent: "from-[#3ec98a] to-[var(--primary)]",
  },
  {
    id: "memory",
    name: "Memory & Revision",
    description: "Flashcards, daily quiz and weak topics",
    icon: Brain,
    accent: "from-[#f2545b] to-[#f5c451]",
  },
];

const t = (
  id: string,
  name: string,
  category: ToolkitTool["category"],
  description: string,
  keywords: string[],
  icon: ToolkitTool["icon"],
  loader: () => Promise<{ default: React.ComponentType }>,
): ToolkitTool => ({ id, name, category, description, keywords, icon, component: lazy(loader) });

export const TOOLKIT_TOOLS: ToolkitTool[] = [
  // Calculators
  t("scientific-calculator", "Scientific Calculator", "calculators",
    "Full expression calculator with trig, logs, powers and factorials.",
    ["scientific", "calculator", "trig", "log", "factorial", "power"], Calculator,
    () => import("@/components/toolkit/tools/scientific-calculator")),
  t("percentage-calculator", "Percentage Calculator", "calculators",
    "Percent of a value, ratios and increase or decrease.",
    ["percentage", "percent", "increase", "decrease", "ratio"], Percent,
    () => import("@/components/toolkit/tools/percentage-calculator")),
  t("question-planner", "Question Planner", "calculators",
    "Split a question target across the days you have left.",
    ["planner", "questions", "target", "daily", "schedule"], Target,
    () => import("@/components/toolkit/tools/question-planner")),
  t("marks-percentage", "Marks vs Percentage", "calculators",
    "Score, accuracy and negative-marking impact for any paper.",
    ["marks", "score", "negative marking", "accuracy", "percentage"], TrendingUp,
    () => import("@/components/toolkit/tools/marks-percentage")),
  t("percentile-estimator", "Percentile Estimator", "calculators",
    "Estimate JEE Main percentile and rank from your marks.",
    ["percentile", "rank", "jee main", "estimate", "nta"], PieChart,
    () => import("@/components/toolkit/tools/percentile-estimator")),

  // Converters
  t("convert-length", "Length", "converters", "Metre, angstrom, light year, inch and more.",
    ["length", "metre", "angstrom", "distance", "inch"], Ruler,
    () => import("@/components/toolkit/tools/convert-length")),
  t("convert-mass", "Mass", "converters", "Kilogram, gram, amu, tonne and pound.",
    ["mass", "weight", "kg", "amu", "gram"], Weight,
    () => import("@/components/toolkit/tools/convert-mass")),
  t("convert-area", "Area", "converters", "Square metre, hectare, acre and barn.",
    ["area", "square", "hectare", "acre"], Square,
    () => import("@/components/toolkit/tools/convert-area")),
  t("convert-volume", "Volume", "converters", "Litre, cubic metre, millilitre and gallon.",
    ["volume", "litre", "cubic", "ml"], Boxes,
    () => import("@/components/toolkit/tools/convert-volume")),
  t("convert-time", "Time", "converters", "Second, minute, hour, day and year.",
    ["time", "second", "hour", "day"], Timer,
    () => import("@/components/toolkit/tools/convert-time")),
  t("convert-speed", "Speed", "converters", "m/s, km/h, mph and Mach.",
    ["speed", "velocity", "kmph", "m/s"], MoveHorizontal,
    () => import("@/components/toolkit/tools/convert-speed")),
  t("convert-temperature", "Temperature", "converters", "Celsius, Kelvin, Fahrenheit and Rankine.",
    ["temperature", "celsius", "kelvin", "fahrenheit"], Thermometer,
    () => import("@/components/toolkit/tools/convert-temperature")),
  t("convert-pressure", "Pressure", "converters", "Pascal, atm, bar, torr and mmHg.",
    ["pressure", "pascal", "atm", "bar", "torr"], Gauge,
    () => import("@/components/toolkit/tools/convert-pressure")),
  t("convert-energy", "Energy", "converters", "Joule, calorie, eV, kWh and erg.",
    ["energy", "joule", "calorie", "ev", "work"], Zap,
    () => import("@/components/toolkit/tools/convert-energy")),
  t("convert-power", "Power", "converters", "Watt, horsepower and calorie per second.",
    ["power", "watt", "horsepower"], CircleGauge,
    () => import("@/components/toolkit/tools/convert-power")),
  t("convert-force", "Force", "converters", "Newton, dyne, kgf and pound-force.",
    ["force", "newton", "dyne", "kgf"], Magnet,
    () => import("@/components/toolkit/tools/convert-force")),
  t("convert-density", "Density", "converters", "kg/m³, g/cm³ and g/L.",
    ["density", "kg/m3", "g/cm3"], Droplets,
    () => import("@/components/toolkit/tools/convert-density")),
  t("si-prefix", "SI Prefixes", "converters", "Shift between tera and femto in one tap.",
    ["si", "prefix", "kilo", "milli", "micro", "nano"], SlidersHorizontal,
    () => import("@/components/toolkit/tools/si-prefix")),
  t("mole-gram", "Mole ↔ Gram", "converters", "Molar mass, moles, grams and particle count.",
    ["mole", "gram", "molar mass", "avogadro", "formula"], Scale,
    () => import("@/components/toolkit/tools/mole-gram")),

  // Chemistry
  t("periodic-table", "Periodic Table", "chemistry",
    "All 118 elements with configurations and JEE notes.",
    ["periodic", "table", "element", "configuration", "atomic"], Grid3x3,
    () => import("@/components/toolkit/tools/periodic-table")),
  t("name-reactions", "Organic Name Reactions", "chemistry",
    "Searchable index of the named reactions JEE repeats.",
    ["organic", "reaction", "named", "aldol", "cannizzaro"], Flame,
    () => import("@/components/toolkit/tools/name-reactions")),
  t("functional-groups", "Functional Groups", "chemistry",
    "Groups, prefixes, suffixes and priority order.",
    ["functional", "group", "iupac", "suffix", "priority"], Beaker,
    () => import("@/components/toolkit/tools/functional-groups")),
  t("inorganic-colors", "Inorganic Colours", "chemistry",
    "Colours of ions, precipitates and flame tests.",
    ["colour", "color", "precipitate", "flame test", "ion"], Sparkles,
    () => import("@/components/toolkit/tools/inorganic-colors")),
  t("salt-analysis", "Salt Analysis", "chemistry",
    "Cation and anion identification in the lab order.",
    ["salt", "analysis", "cation", "anion", "group"], FlaskConical,
    () => import("@/components/toolkit/tools/salt-analysis")),
  t("qualitative-analysis", "Qualitative Tests", "chemistry",
    "Confirmatory tests and the observations that matter.",
    ["qualitative", "test", "confirmatory", "observation"], Droplets,
    () => import("@/components/toolkit/tools/qualitative-analysis")),
  t("electrochemical-series", "Electrochemical Series", "chemistry",
    "Standard reduction potentials in order.",
    ["electrochemical", "sep", "reduction", "potential", "emf"], Zap,
    () => import("@/components/toolkit/tools/electrochemical-series")),
  t("metallurgy", "Metallurgy", "chemistry",
    "Ores, processes and extraction routes.",
    ["metallurgy", "ore", "extraction", "roasting", "calcination"], Blocks,
    () => import("@/components/toolkit/tools/metallurgy")),
  t("solubility-rules", "Solubility Rules", "chemistry",
    "What dissolves, what precipitates, and the exceptions.",
    ["solubility", "soluble", "precipitate", "rules"], Waves,
    () => import("@/components/toolkit/tools/solubility-rules")),

  // Physics
  t("si-units", "SI Units", "physics", "Base and derived units with symbols.",
    ["si", "unit", "base", "derived"], Ruler,
    () => import("@/components/toolkit/tools/si-units")),
  t("physical-constants", "Physical Constants", "physics",
    "Every constant with the value JEE expects.",
    ["constant", "planck", "avogadro", "gravitational", "charge"], Atom,
    () => import("@/components/toolkit/tools/physical-constants")),
  t("dimensions", "Dimensional Formulae", "physics",
    "Dimensions of every quantity in the syllabus.",
    ["dimension", "formula", "mlt", "dimensional analysis"], Layers,
    () => import("@/components/toolkit/tools/dimensions")),
  t("error-analysis", "Error Analysis", "physics",
    "Combining errors in sums, products and powers.",
    ["error", "uncertainty", "significant figures", "least count"], LineChart,
    () => import("@/components/toolkit/tools/error-analysis")),
  t("important-equations", "Key Equations", "physics",
    "Mechanics to modern physics, chapter by chapter.",
    ["equation", "formula", "mechanics", "optics", "modern"], FunctionSquare,
    () => import("@/components/toolkit/tools/important-equations")),

  // Maths
  t("trig-values", "Trigonometry", "maths", "Standard angles, identities and transformations.",
    ["trig", "sin", "cos", "identity", "angle"], Triangle,
    () => import("@/components/toolkit/tools/trig-values")),
  t("algebra", "Algebra", "maths", "Quadratics, progressions, binomial and logs.",
    ["algebra", "quadratic", "ap", "gp", "binomial", "log"], Binary,
    () => import("@/components/toolkit/tools/algebra")),
  t("coordinate-geometry", "Coordinate Geometry", "maths",
    "Lines, circles, parabola, ellipse and hyperbola.",
    ["coordinate", "circle", "parabola", "ellipse", "hyperbola", "line"], Compass,
    () => import("@/components/toolkit/tools/coordinate-geometry")),
  t("vectors", "Vectors", "maths", "Dot, cross, projections and triple products.",
    ["vector", "dot", "cross", "projection"], Radius,
    () => import("@/components/toolkit/tools/vectors")),
  t("geometry-3d", "3D Geometry", "maths", "Lines, planes, distances and angles in space.",
    ["3d", "plane", "line", "distance", "geometry"], Shapes,
    () => import("@/components/toolkit/tools/geometry-3d")),
  t("differentiation", "Differentiation", "maths", "Standard derivatives and the rules.",
    ["differentiation", "derivative", "calculus", "chain rule"], LineChart,
    () => import("@/components/toolkit/tools/differentiation")),
  t("integration", "Integration", "maths", "Standard integrals and substitution patterns.",
    ["integration", "integral", "calculus", "antiderivative"], Sigma,
    () => import("@/components/toolkit/tools/integration")),
  t("probability", "Probability", "maths", "Counting, conditional probability and distributions.",
    ["probability", "bayes", "binomial", "permutation", "combination"], PieChart,
    () => import("@/components/toolkit/tools/probability")),
  t("matrices", "Matrices & Determinants", "maths",
    "Properties, inverses and determinant shortcuts.",
    ["matrix", "determinant", "inverse", "adjoint"], Grid3x3,
    () => import("@/components/toolkit/tools/matrices")),

  // Biology (NEET)
  t("genetics-punnett", "Genetics & Punnett Square", "biology",
    "Interactive Punnett squares plus Mendelian inheritance reference.",
    ["genetics", "punnett", "mendel", "inheritance", "cross", "allele", "neet"], Dna,
    () => import("@/components/toolkit/tools/genetics-punnett")),
  t("human-physiology", "Human Physiology", "biology",
    "Digestion, circulation, excretion, neural and hormonal control.",
    ["physiology", "human", "digestion", "circulation", "excretion", "hormone", "neet"], HeartPulse,
    () => import("@/components/toolkit/tools/human-physiology")),
  t("cell-organelles", "Cell & Organelles", "biology",
    "Cell structure, every organelle and the cell cycle in revision form.",
    ["cell", "organelle", "mitochondria", "nucleus", "ribosome", "neet"], Blocks,
    () => import("@/components/toolkit/tools/cell-organelles")),
  t("biomolecules", "Biomolecules", "biology",
    "Carbohydrates, proteins, lipids, nucleic acids and enzymes.",
    ["biomolecule", "protein", "lipid", "carbohydrate", "dna", "enzyme", "neet"], Sprout,
    () => import("@/components/toolkit/tools/biomolecules")),
  t("ecology", "Ecology & Environment", "biology",
    "Population growth, ecosystems, biodiversity and pollution.",
    ["ecology", "ecosystem", "population", "biodiversity", "environment", "neet"], Globe2,
    () => import("@/components/toolkit/tools/ecology")),

  // Memory
  t("flashcards", "Flashcards", "memory", "Flip through formula cards subject by subject.",
    ["flashcard", "cards", "revision", "memorise"], BookOpen,
    () => import("@/components/toolkit/tools/flashcards")),
  t("daily-quiz", "Daily Quiz", "memory", "Five fresh questions every day.",
    ["quiz", "daily", "mcq", "practice"], Brain,
    () => import("@/components/toolkit/tools/daily-quiz")),
  t("weak-topics", "Weak Topics", "memory", "Everything you missed, collected for revision.",
    ["weak", "topics", "mistakes", "revision"], Target,
    () => import("@/components/toolkit/tools/weak-topics")),
  t("saved-formulae", "Saved Formulae", "memory", "Your starred formulae, constants and reactions.",
    ["saved", "favourite", "starred", "bookmark"], Star,
    () => import("@/components/toolkit/tools/saved-formulae")),
];

export const TOOL_BY_ID = new Map(TOOLKIT_TOOLS.map((tool) => [tool.id, tool]));

export function categoryOf(id: string) {
  return TOOLKIT_CATEGORIES.find((c) => c.id === id);
}

export function searchTools(query: string): ToolkitTool[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return TOOLKIT_TOOLS.filter((tool) =>
    `${tool.name} ${tool.description} ${tool.keywords.join(" ")}`.toLowerCase().includes(q),
  ).slice(0, 12);
}
